/**
 * "acting on: {selected}" chip for the Shogun panel. When the client has a selected in-view record, this shows
 * the user what "this"/"the current item" will resolve to before they confirm a
 * Shogun action.
 *
 * PROPS-DRIVEN — it fetches nothing and holds no state. The host
 * (`useShogunViewContext` → `PanelChat`) computes the selected record from the
 * live route/selection and passes it in. Renders nothing when `selected` is
 * absent. Covered by a Storybook functional story per the tier rubric.
 */

import type { ViewRecord } from '@localagent/sdk';

interface ViewContextIndicatorProps {
  /** The focused record the agent will treat as "this", or undefined. */
  selected?: ViewRecord;
}

export function ViewContextIndicator({ selected }: ViewContextIndicatorProps) {
  if (!selected) return null;

  // Prefer the human label; fall back to the opaque id so the chip is never
  // empty (a selected record always has an id).
  const shown = selected.label && selected.label.trim() !== '' ? selected.label : selected.id;

  return (
    <div
      data-testid="view-context-indicator"
      className="flex items-center gap-1 px-2 py-1 text-[11px] text-muted-foreground"
    >
      <span>acting on:</span>
      <code
        data-testid="view-context-indicator-target"
        className="rounded bg-muted px-1 py-0.5 text-[11px] text-foreground"
      >
        {shown}
      </code>
    </div>
  );
}
